import posthog from 'posthog-js'
import { ref } from 'vue'

let isReady = false
const queue = []

/** Multivariate experiment flag driving the Inventory redesign rollout. */
export const INVENTORY_BETA_FLAG = 'inventory-beta'
export const INVENTORY_BETA_TEST = 'test'
export const INVENTORY_BETA_CONTROL = 'control'

/**
 * The `inventory-beta` variant for the current user: `'test'`, `'control'`, or
 * `undefined` while flags are still unresolved (see `isInventoryBetaResolved`).
 *
 * A `ref`, not a plain read, because the gate is consumed by a Pinia getter and
 * dozens of `computed`s — PostHog resolves flags asynchronously and again after
 * `identify()`, so consumers have to re-evaluate when the answer arrives.
 */
export const inventoryBetaVariant = ref(undefined)

/** `false` until PostHog has produced a value (cached or fresh) for the flag. */
export const isInventoryBetaResolved = ref(false)

/**
 * `true` when PostHog has put this user in the beta's test variant — the
 * PostHog half of the gate, OR-ed with the email allowlist in
 * `UserStore.isInventoryPilot` (the only consumer, and the single predicate
 * every part of the beta reads).
 *
 * `true` counts alongside `'test'`: that's what a BOOLEAN flag returns, so
 * configuring `inventory-beta` as boolean rather than multivariate degrades to
 * "everyone targeted is in" instead of locking the whole cohort out.
 */
export const isInventoryBetaTest = () =>
  inventoryBetaVariant.value === INVENTORY_BETA_TEST || inventoryBetaVariant.value === true

/**
 * Read the flag through `getFeatureFlag` rather than the `onFeatureFlags`
 * callback payload, because that call is what emits `$feature_flag_called` —
 * the exposure event the experiment needs to attribute a user to a variant.
 * PostHog de-dupes it per flag+value per session, so calling it on every
 * refresh is cheap.
 */
const syncInventoryBeta = () => {
  inventoryBetaVariant.value = posthog.getFeatureFlag(INVENTORY_BETA_FLAG)
  isInventoryBetaResolved.value = true
}

export const markPosthogReady = () => {
  isReady = true
  queue.forEach(fn => fn())
  queue.length = 0

  // By the time `loaded` fires, PostHog has rehydrated the flags it persisted
  // last session — so a returning beta user is resolved before first paint and
  // never flickers through the classic app.
  syncInventoryBeta()

  // Fires again on the fresh `/flags` response and after every `identify()`,
  // which is when an anonymous visitor first becomes a known experiment
  // participant.
  posthog.onFeatureFlags(syncInventoryBeta)
}

export const safeCapture = (event, properties = {}) => {
  if (isReady) {
    posthog.capture(event, properties)
  } else {
    queue.push(() => posthog.capture(event, properties))
  }
}

export const safeIdentify = (distinctId, properties = {}) => {
  if (isReady) {
    posthog.identify(distinctId, properties)
  } else {
    queue.push(() => posthog.identify(distinctId, properties))
  }
}

/**
 * Make sure PostHog knows who this is, whether or not they signed in during
 * this session. `safeIdentify` only runs on the sign-in and account-creation
 * paths, so a returning user restoring a persisted session was evaluated
 * against whatever distinct id the browser happened to hold — fine for
 * autocapture, not fine for an experiment that targets specific people.
 *
 * A no-op once the ids already match, so it can be called from a `user` watcher
 * without re-identifying on every profile change.
 */
export const ensureIdentified = (user) => {
  if (!user?.id) return

  const distinctId = String(user.id)

  const apply = () => {
    if (posthog.get_distinct_id() === distinctId) return

    posthog.identify(distinctId, {
      email: user.email,
      name: user.name,
      business_name: user.business_name || '',
    })
  }

  isReady ? apply() : queue.push(apply)
}

/**
 * Pin the local user to a variant, e.g. to honour an opt-out without waiting on
 * a PostHog cohort change. Persisted by PostHog (`$override_feature_flags`), so
 * it survives reloads until cleared with `clearInventoryBetaOverride()`.
 *
 * This is a CLIENT-side override for consistency of what the user sees; the
 * durable opt-out is still `user.data.about.new_design` on the account, which is
 * what a different browser or a cleared cache reads.
 *
 * Note the API: `posthog.overrideFeatureFlag(...)` from the docs does not exist
 * on the top-level object in posthog-js 1.393 — it lives on `featureFlags`, and
 * the singular `override()` there is deprecated in favour of this one.
 */
export const overrideInventoryBeta = (variant) => {
  const apply = () => {
    posthog.featureFlags?.overrideFeatureFlags?.({ flags: { [INVENTORY_BETA_FLAG]: variant } })
    syncInventoryBeta()
  }

  isReady ? apply() : queue.push(apply)
}

/**
 * Drop the local override and fall back to whatever PostHog assigns. Clears
 * ALL client overrides — the only one we ever set is `inventory-beta`, but a
 * PostHog toolbar override set for debugging would go with it.
 */
export const clearInventoryBetaOverride = () => {
  const apply = () => {
    posthog.featureFlags?.overrideFeatureFlags?.(false)
    syncInventoryBeta()
  }

  isReady ? apply() : queue.push(apply)
}
